import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";

import { Dialog } from "../../../../../../../utils/Dialog/Dialog.component";
import FormSet from "../../../../../../../utils/forms/components/FormSet";
import { ConfigurationsActionCreators } from "../../../../../actions/actionCreators";
import {
    ProgressiveConfig,
    SpecialPrizeConfiguration,
    GameGroupConfiguration
} from "../../../../../reducers/editingConfiguration.reducer";
import { configurationsStoreSelectors } from "../../../../../store/store";
import gameSubform from "./FormBuild/GameGroups.form";

import "./JackpotGameGroups.component.scss";
import { checkProgressivesHaveGroups } from "../../../../Utils/parse-available-groups";

const useStateSlices = () => {
    const {
        gameGroupsSchema,
        gameGroupsConfig,
        progressivesConfig,
        gameInstances,
        selectedGameInstances,
        specialPrizesConfig,
        settingsConfig,
        gameBundles
    } = configurationsStoreSelectors;

    const schema = useSelector(gameGroupsSchema);
    const groupsConfigSlice = useSelector(gameGroupsConfig);
    const gameInstancesList = useSelector(gameInstances);
    const gameBundlesList = useSelector(gameBundles);
    const progressivesConfigs = useSelector(progressivesConfig);
    const specialPrizesConfigs = useSelector(specialPrizesConfig);
    const selectedGameInstancesList = useSelector(selectedGameInstances);
    const settingsObject = useSelector(settingsConfig);

    return {
        schema,
        groupsConfig: groupsConfigSlice,
        progressivesConfigs,
        specialPrizesConfigs,
        gameInstancesList,
        gameBundlesList,
        selectedGameInstancesList,
        gameTypes: settingsObject ? settingsObject.game_types : []
    };
};

const useDispatchGroupConfig = () => {
    const dispatch = useDispatch();
    const { groupsConfig, gameBundlesList } = useStateSlices();
    const progressivesConfigs = useSelector(configurationsStoreSelectors.progressivesConfig);
    const currentFormState = useSelector(configurationsStoreSelectors.configFormValid);

    return formEvent => {
        const { values: config, isValid } = formEvent;

        const prevBundles = groupsConfig
            .reduce((accum, cur) => {
                return accum.concat(cur["game_bundles"]);
            }, [])
            .sort();
        const currentBundles = config
            .reduce((accum, cur) => {
                return accum.concat(cur.game_bundles);
            }, [])
            .sort();

        if (!_.isEqual(prevBundles, currentBundles)) {
            //console.log("CHANGED GAME_BUNDLES - UPDATE GAMES SELECTED");

            const removed = _.difference(prevBundles, currentBundles);
            const added = _.difference(currentBundles, prevBundles);

            /*if (removed.length > 0) {
                removed.map(rm => {
                    const removedBundle = gameBundlesList.find(bundle => bundle.id == rm);
                    const currentBundles
                })
                // Remove games from selected

                // get config where is removed
            }*/

            if (added.length > 0) {
                added.map(ad => {
                    const addedBundle = gameBundlesList.find(
                        bundle => bundle.id == ad
                    );
                    const currentBundleIndex = config.findIndex(conf =>
                        conf.game_bundles.includes(addedBundle.id)
                    );
                    config[currentBundleIndex].game_instances = config[
                        currentBundleIndex
                    ].game_instances.concat(
                        addedBundle.games.map(game => game.id)
                    );
                });
                // Added games as selected
            }
        }


        dispatch(
            ConfigurationsActionCreators.updateGroupsConfig({
                config,
                isValid,
                type: "Game_Group"
            })
        );

        dispatch(
            ConfigurationsActionCreators.updateProgressivesConfig({
                config: progressivesConfigs,
                isValid: currentFormState['progressives']
                    ? checkProgressivesHaveGroups(progressivesConfigs,"game_groups")
                    : currentFormState['progressives'],
                groupType: "game"
            })
        );
    };
};

const useCheckValidityEffect = () => {
    const currentGroupConfig = useSelector(
        configurationsStoreSelectors.gameGroupsConfig
    );
    const currentFormState = useSelector(
        configurationsStoreSelectors.configFormValid
    );
    const dispatch = useDispatch();
    useEffect(() => {
        if (currentGroupConfig.length === 0 && currentFormState["groups"]) {
            dispatch(
                ConfigurationsActionCreators.updateGroupsConfig({
                    config: [],
                    isValid: false
                })
            );
        }
    }, [currentGroupConfig]);
};

const useDeleteGroupDialog = (
    groupsConfig: GameGroupConfiguration[],
    progressivesConfigs: ProgressiveConfig[],
    specialPrizesConfigs: SpecialPrizeConfiguration[]
) => {
    const defaultState = {
        showDialog: false,
        indexToDelete: null,
        deleteCallback: null
    };
    const [deleteState, setDeleteState] = useState(defaultState);

    const _checkIfGroupInProgressives = (index: number) => {
        const group = groupsConfig[index];
        let isPresent = false;
        isPresent = progressivesConfigs.some(item => {
            if (
                item.type == "levels" ||
                item.type == "easylink" ||
                item.type == "sap" ||
                item.type == "multipots"||
                item.type == "jumbolink" ||
                item.type == "reelstrike" ||
                item.type == "luckytokenslink"
            ) {
                if (item.game_groups && item.game_groups.includes(group.id)) {
                    return true;
                } else {
                    let tempConst = false;
                    item["children"].map(child => {
                        if (child.game_groups.includes(group.id))
                            tempConst = true;
                    });
                    return tempConst;
                }
            } else {
                return item.game_groups.includes(group.id);
            }
        });

        if (!isPresent) {
            isPresent = specialPrizesConfigs.some(item =>
                item.game_groups.includes(group.id)
            );
        }

        return isPresent;
    };

    const onDeleteGroup = (deleteCallback, index) => {
        if (_checkIfGroupInProgressives(index)) {
            setDeleteState({
                showDialog: true,
                indexToDelete: index,
                deleteCallback
            });
        } else {
            deleteCallback(index);
            setDeleteState(defaultState);
        }
    };

    const onDialogConfirm = () => {
        deleteState.deleteCallback(deleteState.indexToDelete);
        setDeleteState(defaultState);
    };

    const onDialogClose = () => setDeleteState(defaultState);

    return {
        showDialog: deleteState.showDialog,
        onDeleteGroup,
        onDialogConfirm,
        onDialogClose
    };
};

const GameGroups = () => {
    const {
        schema,
        groupsConfig,
        progressivesConfigs,
        specialPrizesConfigs,
        gameInstancesList,
        gameBundlesList,
        selectedGameInstancesList,
        gameTypes
    } = useStateSlices();
    const { showDialog, onDeleteGroup, onDialogConfirm, onDialogClose } =
        useDeleteGroupDialog(
            groupsConfig,
            progressivesConfigs,
            specialPrizesConfigs
        );
    const onChangeHandler = useDispatchGroupConfig();
    useCheckValidityEffect();

    const gameFormSet = useRef();

    const addNewGame = ref => {
        ref.current.onAdd(0);
    };

    let schemaProperties = { ...schema.items.properties };
    schemaProperties["game_instances"] = schemaProperties["games"];
    delete schemaProperties.games;
    let newSchema = {
        ...schema,
        items: {
            ...schema.items,
            properties: schemaProperties,
            required: ["id", "name", "game_instances"]
        }
    };
    const currentSubsidiaryId = useSelector(
        configurationsStoreSelectors.editingConfiguration
    );
    const forms = gameSubform({
        schema: newSchema,
        groupsConfig,
        gameInstances: gameInstancesList,
        gameBundles: gameBundlesList,
        selectedGameInstances: selectedGameInstancesList,
        gameTypes,
        currentSubsidiaryId: currentSubsidiaryId.subsidiary.id
    });

    return (
        <div id="game-groups-container">
            <div className="inner-card">
                <div className="inner-card-title">
                    <p>
                        Groups
                        <span>{` (${groupsConfig.length})`}</span>
                    </p>
                    <button onClick={() => addNewGame(gameFormSet)}>
                        <i className="icon-plus-light"></i>
                    </button>
                </div>
                <div className="inner-card-content">
                    <FormSet
                        subFormTypes={forms}
                        ref={gameFormSet}
                        initialValues={groupsConfig}
                        onChange={onChangeHandler}
                        onDelete={onDeleteGroup}
                        hideAdd
                        isConfig={true}
                    />
                    <Dialog
                        open={showDialog}
                        onConfirm={onDialogConfirm}
                        onClose={onDialogClose}
                    >
                        You are about to delete a game group that may be
                        included in a Progressive/Special Prize configuration.
                        Are you sure?
                    </Dialog>
                </div>
            </div>
        </div>
    );
};

export default GameGroups;
